import { getOrderDescriptor, objectReduce } from '@caresend/utils';
import '@caresend/types';
import 'firebase/auth';
import '../../../../../database/firebaseConfig/cloudFunctions.js';
import { isProd } from '../../../../../database/firebaseConfig/init.js';
import '../../../../../database/firebaseConfig/methods.js';
import '@sentry/vue';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import '../../../../../components/ui/Icon.vue.js';
import '../../../../../functions/configuration/icons.js';
import '../../../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../../../functions/tracking/index.js';
import '../../../../../router/helpers/initRouter.js';
import { getStore } from '../../../../helpers/initStore.js';
import '@firebase/auth';
import 'browser-image-compression';
import 'firebase/storage';
import 'js-sha256';

/**
 * Check the patient shipment info to determine if the office affiliation is valid
 * for the draft waypoint's creating office. This is used by the supply filter
 * to determine if visit supplies can be fulfilled with a shipment
 */
const getPatientShipmentOfficeAffiliationFailure = (supplyOwnerInfo, warehouseFallback) => {
    const store = getStore();
    const { draftWaypoint } = store.state.schedulingFlow;
    if (!draftWaypoint)
        return 'Missing draft waypoint';
    const { officeID, createdByOfficeID } = draftWaypoint;
    const { allOfficeAffiliations, officeAffiliations, ownerID } = supplyOwnerInfo;
    if (allOfficeAffiliations)
        return null;
    if (!officeAffiliations)
        return 'Missing office affiliations';
    const fallbackWarehouseID = isProd ? 'oaou1noi2iglflbe62h' : 'gK6v4nQANBX4iQSWqdzw';
    if (!warehouseFallback && ownerID === fallbackWarehouseID) {
        return 'Cannot use fallback warehouse';
    }
    Object.keys(officeAffiliations).forEach((officeAffiliationID) => {
        if ([officeID, createdByOfficeID].includes(officeAffiliationID))
            return null;
    });
    return 'Office affiliation not compatible';
};
/**
 * Check if the patient shipment is disabled on the office. If so,
 * the office only accepts nurses with their own supplies
 */
const getPatientShipmentDisabledOnOffice = (draftWaypoint) => {
    var _a;
    const store = getStore();
    const { officeID, createdByOfficeID } = draftWaypoint;
    const officeIDToFetch = createdByOfficeID !== null && createdByOfficeID !== void 0 ? createdByOfficeID : officeID;
    if (!officeIDToFetch)
        return false;
    return !!((_a = store.state.offices.offices[officeIDToFetch]) === null || _a === void 0 ? void 0 : _a.patientShipmentDisabled);
};
const getPackingDeadline = (patientShipmentInfo, orderDescriptor, isMailInVisit) => {
    if (orderDescriptor === 'draft') {
        return patientShipmentInfo.packingDeadlineDraft;
    }
    if (isMailInVisit && patientShipmentInfo.packingDeadlineFreezing) {
        return patientShipmentInfo.packingDeadlineFreezing;
    }
    return patientShipmentInfo.packingDeadlineFull;
};
const getEarliestPatientShipmentPackingDeadline = (timeSlot) => {
    var _a;
    const store = getStore();
    const { draftWaypoint } = store.state.schedulingFlow;
    const orderDescriptor = getOrderDescriptor(draftWaypoint);
    const cheapestShipmentInfo = (_a = store.state.schedulingFlow.cheapestPatientShipmentInfo) === null || _a === void 0 ? void 0 : _a[timeSlot.startTime];
    if (!cheapestShipmentInfo)
        return;
    return getPackingDeadline(cheapestShipmentInfo, orderDescriptor, false);
};
/**
 * Get the required supplies from the procedure supply instructions
 * This is used by the supply filter to determine if a visit can be fulfilled
 * based on the procedures' supply requirements
 */
const getRequiredSuppliesFromProcedures = (procedures, supplyInstructions) => {
    const allRequiredSupplies = new Set();
    procedures.forEach((procedureID) => {
        const supplyInstructionsForProcedure = supplyInstructions[procedureID];
        if (!(supplyInstructionsForProcedure === null || supplyInstructionsForProcedure === void 0 ? void 0 : supplyInstructionsForProcedure.supplyInstructionsByPlaceGroup))
            return;
        Object.entries(supplyInstructionsForProcedure.supplyInstructionsByPlaceGroup)
            .forEach(([_, supplyInstructionConfig]) => {
            var _a, _b;
            const supplyInstructionsConfiguration = (_a = supplyInstructionConfig.supplyInstructionsByDeliveryType.walkIn) !== null && _a !== void 0 ? _a : supplyInstructionConfig.supplyInstructionsByDeliveryType.mailIn;
            if (!supplyInstructionsConfiguration)
                return;
            const supplyRequirements = Object.keys((_b = supplyInstructionsConfiguration === null || supplyInstructionsConfiguration === void 0 ? void 0 : supplyInstructionsConfiguration.supplyInstructions) !== null && _b !== void 0 ? _b : {});
            supplyRequirements.forEach((supplyID) => allRequiredSupplies.add(supplyID));
        });
    });
    return Array.from(allRequiredSupplies);
};
/**
 * Check if the supplies meet the procedure supply requirements
 */
const getProcedureSupplyRequirementsMet = (supplies, requiredSupplies) => requiredSupplies.every((supplyID) => supplies.includes(supplyID));
const isPatientShipmentInfoPackingDeadlineValid = (patientShipmentInfo, orderDescriptor, isMailInVisit) => {
    const packingDeadline = getPackingDeadline(patientShipmentInfo, orderDescriptor, isMailInVisit);
    const currentTime = Date.now();
    const meetsPackingDeadline = currentTime < packingDeadline;
    return meetsPackingDeadline;
};
const filterPatientShipmentInfoByDeliveryType = (shipmentInfoByType, orderDescriptor, isMailInVisit) => objectReduce(shipmentInfoByType, (acc, [key, value]) => {
    if (isPatientShipmentInfoPackingDeadlineValid(value, orderDescriptor, isMailInVisit)) {
        acc[key] = value;
    }
    return acc;
}, {});
const filterPatientShipmentInfoByCarrier = (supplyOwnerInfo, orderDescriptor, isMailInVisit) => {
    const { shipmentInfoByType } = supplyOwnerInfo;
    const filteredShipmentInfoByType = filterPatientShipmentInfoByDeliveryType(shipmentInfoByType, orderDescriptor, isMailInVisit);
    return {
        ...supplyOwnerInfo,
        shipmentInfoByType: filteredShipmentInfoByType,
    };
};
/**
 * Get the patient shipment errors
 * This is used by the supply filter to determine if a visit can be fulfilled
 * based on the patient shipment info's office affiliation and supply requirements
 */
const getPatientShipmentErrors = (supplyOwnerInfo, warehouseFallback, requiredSupplies) => {
    var _a;
    const officeAffiliationFailure = getPatientShipmentOfficeAffiliationFailure(supplyOwnerInfo, warehouseFallback);
    if (officeAffiliationFailure)
        return officeAffiliationFailure;
    const supplies = Object.keys((_a = supplyOwnerInfo.supplies) !== null && _a !== void 0 ? _a : {});
    if (supplyOwnerInfo.allSupplies)
        return null;
    if (!supplies.length)
        return 'Missing supplies';
    if (!getProcedureSupplyRequirementsMet(supplies, requiredSupplies)) {
        return 'Supplies do not meet procedure requirements';
    }
    return null;
};
/**
 * Returns the nurses that have the required supplies
 */
const filterNursesWithSupplies = (availableNurses, requiredSupplies) => Object.fromEntries(Object.entries(availableNurses).filter(([_, nurse]) => {
    if (nurse.allSupplies)
        return true;
    if (!nurse.supplies)
        return false;
    const nurseSupplyIDs = Object.keys(nurse.supplies);
    return getProcedureSupplyRequirementsMet(nurseSupplyIDs, requiredSupplies);
}));
/**
 * Get the cheapest patient shipment info for all of a time slot's
 * patient shipment info
 */
const getCheapestPatientShipmentInfo = (patientShipmentInfo, time, draftWaypoint, warehouseFallback, requiredSupplies) => {
    var _a;
    const orderDescriptor = getOrderDescriptor(draftWaypoint);
    const eligibleShipmentInfos = Object.values((_a = patientShipmentInfo[time]) !== null && _a !== void 0 ? _a : {})
        .map((supplyOwnerInfo) => {
        const filteredPatientShipmentInfo = filterPatientShipmentInfoByCarrier(supplyOwnerInfo, orderDescriptor, false);
        return filteredPatientShipmentInfo;
    })
        .filter((filteredPatientShipmentInfo) => {
        const patientShipmentErrors = getPatientShipmentErrors(filteredPatientShipmentInfo, warehouseFallback, requiredSupplies);
        return !patientShipmentErrors;
    });
    const sortedShipmentInfos = eligibleShipmentInfos
        .flatMap((shipmentInfo) => Object.values(shipmentInfo.shipmentInfoByType))
        .sort((a, b) => a.shipmentCost - b.shipmentCost);
    return sortedShipmentInfos.length > 0 ? sortedShipmentInfos[0] : undefined;
};
/**
 * Get the latest supply arrival timestamp from all of the supply instructions'
 * supply shipment configs. Used to determine if the office-shipped supplies
 * arrive before the time slot start time
 */
const getLatestSupplyArrivalTimestamp = (supplyInstructionsByProcedure) => Object.values(supplyInstructionsByProcedure)
    .reduce((latestTimestamp, supplyInstructions) => {
    var _a;
    const { supplyArrivalTimestamp } = (_a = supplyInstructions.supplyShipmentConfig) !== null && _a !== void 0 ? _a : {};
    return Math.max(latestTimestamp, supplyArrivalTimestamp !== null && supplyArrivalTimestamp !== void 0 ? supplyArrivalTimestamp : Date.now());
}, Date.now());
/**
 * Returns an error if the office-shipped supplies arrive after the time slot
 * start time
 */
const getSupplyArrivalTimeError = (timeSlot, latestSupplyArrivalTimestamp) => {
    if (latestSupplyArrivalTimestamp >= timeSlot.startTime) {
        return 'Office-shipped supplies must arrive before the time slot start time';
    }
    return null;
};

export { filterNursesWithSupplies, getCheapestPatientShipmentInfo, getEarliestPatientShipmentPackingDeadline, getLatestSupplyArrivalTimestamp, getPackingDeadline, getPatientShipmentDisabledOnOffice, getPatientShipmentOfficeAffiliationFailure, getRequiredSuppliesFromProcedures, getSupplyArrivalTimeError };
